/* eslint-disable @typescript-eslint/naming-convention */
import {
    PaymentMethod,
    PaymentMethodMeta,
    OnPayExtraParams,
    OrderData,
    OrderInterruptionAlertData,
    InvalidCouponAlertData,
    JuspayTokenizationMeta,
    CardDetails,
    CreatePennyTransactionResponse,
    ConfirmPennyTransactionResponse,
    VALID_PAYMENT_METHODS,
    ApiResponseJuspayMeta,
    OnPayArbitaryData,
    PaymentFailureResponse,
    OrderIdMismatchAlertPayload,
} from "@swiggy-private/payment-types";

import { NativeLocalCart, ApiMenuCartItem, IIrctcPayload } from "@swiggy-private/cart-types";

export interface PlaceOrderData {
    paymentMethodData: PaymentMethod<PaymentMethodMeta>;
    extraParams?: OnPayExtraParams;
    extraArbitaryData?: OnPayArbitaryData;
}

export interface TransactionReqData {
    cardDetails: CardDetails;
    paymentMethod: typeof VALID_PAYMENT_METHODS.CARD | typeof VALID_PAYMENT_METHODS.NEW_CARD;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extraParams?: any;
    // flag for Non Tokenizable card or card with no consent
    isNonSecureCard?: boolean;
}

export interface CheckoutActionPayload {
    placeOrderData: PlaceOrderData;
}

export interface TransactionActionPayload {
    transactionReqData: TransactionReqData;
    useJuspaySDK?: boolean;
}

interface AmazonPaySdkContextValue {
    state: {
        available: boolean;
        linked: boolean;
        isBalanceSufficient: boolean;
        balance?: string;
    } | null;
    setState: (state: AmazonPaySdkContextValue["state"]) => void;
    sdkFetchInProgress: boolean;
}

interface WebviewRefreshExtraParams {
    webviewRefreshed?: boolean;
    redirectToPaymentPageWhenWebviewRefreshed?: boolean;
    amazonPayTransactionId?: string;
}

export interface ProcessOrderActionPayload extends CheckoutActionPayload {
    order: OrderData;
    useJuspaySDK?: boolean;
    juspayMeta?: ApiResponseJuspayMeta;
    isPhonepeSdkAvailable?: boolean;
    apayState?: AmazonPaySdkContextValue["state"];
    webviewRefreshExtraParams?: WebviewRefreshExtraParams;
    startTime?: number;
}

export interface ConfirmOrderActionPayload extends CheckoutActionPayload {
    orderId: string;
    isOrderProcessed?: boolean;
    order: OrderData;
}

export interface FailedOrderActionPayload extends CheckoutActionPayload {
    paymentFailureResponse?: PaymentFailureResponse;
    isFallbackApiEnabled?: boolean;
    failedOrder?: OrderData;
}

export interface FailedTransactionActionPayload extends TransactionActionPayload {
    transaction: CreatePennyTransactionResponse | ConfirmPennyTransactionResponse;
}

export interface OrderInterruptionAlertPayload extends CheckoutActionPayload {
    orderInterruptionAlertData: OrderInterruptionAlertData;
    errorStatusCode: number;
    errorCodes?: string[];
}

export interface WebviewRefreshOrderIdMismatchAlertPayload {
    alertData: OrderIdMismatchAlertPayload;
}

export interface InvalidCouponAlertPayload extends CheckoutActionPayload {
    invalidCouponResponseData: InvalidCouponAlertData;
}
export interface VpaTimerActionPayload extends CheckoutActionPayload {
    order: OrderData;
    upiCollectStartTime: number;
}
export interface UpiIntentPollerActionPayload extends CheckoutActionPayload {
    order: OrderData;
    startTime?: number;
}

export interface PayLaterSimplActionSuccessPayload {
    paymentCode: string;
}
export interface PayLaterSimplActionFailurePayload {
    error?: boolean;
    paymentCode: string;
}

export interface ShowPaymentsPayload {
    reload?: boolean;
    showIrctcCart?: boolean;
}

export enum POST_ORDER_STATUS {
    PROCESSING = "processing",
    PENDING = "pending",
}

export interface ProcessTransactionActionPayload extends TransactionActionPayload {
    transaction: CreatePennyTransactionResponse;
    meta?: JuspayTokenizationMeta;
    merchantInfo?: {
        merchantId: string;
        merchantName: string;
        returnToUrl: string;
    };
}

export interface ConfirmTransactionActionPayload extends TransactionActionPayload {
    orderId: string;
    isOrderProcessed?: boolean;
    transaction: CreatePennyTransactionResponse | ConfirmPennyTransactionResponse;
}

export interface PollPennyTransactionStatusActionPayload extends TransactionActionPayload {
    orderId: string;
    isOrderProcessed?: boolean;
    transaction: CreatePennyTransactionResponse & ConfirmPennyTransactionResponse;
}

export interface PayOnDeliveryPaymentAlreadyDoneActionPayload extends CheckoutActionPayload {
    interruptData: {
        title: string;
        orderId: string;
    };
}

export interface AutoPayFailedOrderActionPayload extends FailedOrderActionPayload {
    // values to be same as in EXPERIMENT_VALUES in payment-client package
    autoPayPaymentFailureVariant?: "CONSENT" | "SUBTLE_AUTOPLACE";
}

export interface CartRendererActionPayload {
    skipCartReload?: boolean;
}

export interface DineoutCartRendererActionPayload {
    isEventBookingFlow?: boolean;
    skipCartReload?: boolean;
}

export enum CouponPageType {
    RESTAURANT_COUPON_LISTING = "RESTAURANT",
    PAYMENT_COUPON_LISTING = "PAYMENT",
    GIFTING_COUPON_LISTING = "GIFTING_CART",
}

export interface CouponListingActionPayload {
    couponListingLaunchTimestamp: number;
    couponListingType?: CouponPageType;
}

export interface SharedCartPayload {
    groupCartId: string;
}
export interface UnlockGroupCartActionPayload {
    nativeLocalMergedCart: NativeLocalCart;
}

export enum FlowType {
    M2C = "M2C",
    LINE_ITEM = "LINE_ITEM",
    EXPRESS_HALF_CARD = "EXPRESS_HALF_CARD",
    FREEDEL_HALF_CARD = "FREEDEL_HALF_CARD",
    COMPARISON = "COMPARISON",
}
export enum CART_TYPE {
    REGULAR = "regular",
    GIFTING = "GIFTING",
}

export interface UpdateCartData {
    address_id?: string;
    p_segment_id?: string;
    parent_restaurant_id?: string;
    cartItems?: ApiMenuCartItem[];
    couponCode?: string;
    mealItems?: [];
    restaurantId?: number | string;
    restaurantName?: string;
    tipDetails?: {
        amount: number;
        optIn: boolean;
    };
    subscriptionItems?: {
        plan_id: string;
        quantity: number;
    }[];
    cartType?: CART_TYPE;
    selected_drop_off_instruction_ids?: string[];
    selected_sla_option?: string;
    customer_selected_sla_option?: string;
    slot_id?: string;
    date?: string;
    is_membership_plan_removed?: boolean;
    is_incognito_cart?: boolean;
    irctc_data?: IIrctcPayload;
    delivery_type_opt_in?: boolean | null;
    created_from_group_cart?: boolean;
    subscriptionNudgeInitiator?: FlowType;
}
export interface DineoutSuccessActionPayload {
    orderId?: string;
    isCheckoutFlow?: boolean;
}
